import React, { useState } from 'react';
import { useQuery } from 'react-query';
import api from '../utils/axios';
import {
  ShoppingBag,
  PoundSterling,
  Wallet,
  TrendingUp,
  BarChart3,
  Calendar,
  RefreshCw,
} from 'lucide-react';

// Default range: last 30 days, ending today
const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(Number(amount) || 0);

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white rounded-xl shadow-sm border border-secondary-100 p-5 flex flex-col justify-between min-h-[120px]">
    <div className="flex items-start justify-between">
      <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500 leading-tight">
        {label}
      </p>
      <div className={`p-2 rounded-lg ${accent.bg} shrink-0`}>
        <Icon className={`h-4 w-4 ${accent.icon}`} />
      </div>
    </div>
    <p className="text-2xl font-bold text-secondary-900 mt-3 truncate">
      {value}
    </p>
  </div>
);

const Payouts = () => {
  // Date range that's been *committed* (drives the actual fetch)
  const [appliedRange, setAppliedRange] = useState({
    from: daysAgo(30),
    to: today(),
  });
  // Date range that's currently typed in the inputs (not yet fetched)
  const [draftRange, setDraftRange] = useState(appliedRange);

  const { data, isFetching, refetch, isError } = useQuery(
    ['vendor-financial-reports', appliedRange.from, appliedRange.to],
    async () => {
      const response = await api.get('/vendor/financial-reports', {
        params: {
          dateFrom: appliedRange.from,
          dateTo: appliedRange.to,
        },
      });
      return response.data || {};
    },
    {
      keepPreviousData: true,
      retry: 1,
    }
  );

  const summary = data?.summary || {};
  const totalOrders = summary.orderCount || 0;
  const totalPrice = summary.totalRevenue || 0;
  const servicePrice = summary.totalServiceFees || 0;
  const revenue = summary.totalVendorPayouts || 0;
  const avgOrderValue = summary.averageOrderValue || 0;

  const handleFetch = (e) => {
    e.preventDefault();
    if (!draftRange.from || !draftRange.to) return;
    if (new Date(draftRange.from) > new Date(draftRange.to)) return;
    setAppliedRange(draftRange);
  };

  const rangeInvalid =
    !draftRange.from ||
    !draftRange.to ||
    new Date(draftRange.from) > new Date(draftRange.to);

  return (
    <div className="min-h-screen bg-secondary-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-secondary-900">
          Payout Dashboard
        </h1>
        <p className="text-secondary-600 mt-2">
          Choose a date range to see your sales summary.
        </p>
      </div>

      {/* Date filter form */}
      <form
        onSubmit={handleFetch}
        className="bg-white rounded-xl shadow-sm border border-secondary-100 p-6 mb-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">
              <Calendar className="inline h-4 w-4 mr-1 -mt-1" />
              From Date
            </label>
            <input
              type="date"
              value={draftRange.from}
              max={draftRange.to || today()}
              onChange={(e) =>
                setDraftRange((r) => ({ ...r, from: e.target.value }))
              }
              className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">
              <Calendar className="inline h-4 w-4 mr-1 -mt-1" />
              To Date
            </label>
            <input
              type="date"
              value={draftRange.to}
              min={draftRange.from}
              max={today()}
              onChange={(e) =>
                setDraftRange((r) => ({ ...r, to: e.target.value }))
              }
              className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex space-x-2">
            <button
              type="submit"
              disabled={rangeInvalid || isFetching}
              className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isFetching ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Fetching…
                </>
              ) : (
                'Fetch'
              )}
            </button>
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center justify-center px-3 py-2 bg-white border border-secondary-300 rounded-lg text-secondary-700 hover:bg-secondary-50 disabled:opacity-50"
              title="Refresh current range"
            >
              <RefreshCw
                className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
              />
            </button>
          </div>
        </div>
      </form>

      {/* Error state */}
      {isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
          Failed to load report. Please try again.
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        <StatCard
          icon={ShoppingBag}
          label="Total Orders"
          value={totalOrders}
          accent={{ bg: 'bg-blue-100', icon: 'text-blue-600' }}
        />
        <StatCard
          icon={PoundSterling}
          label="Total Price"
          value={formatCurrency(totalPrice)}
          accent={{ bg: 'bg-emerald-100', icon: 'text-emerald-600' }}
        />
        <StatCard
          icon={Wallet}
          label="Service Price"
          value={formatCurrency(servicePrice)}
          accent={{ bg: 'bg-amber-100', icon: 'text-amber-600' }}
        />
        <StatCard
          icon={TrendingUp}
          label="Revenue"
          value={formatCurrency(revenue)}
          accent={{ bg: 'bg-green-100', icon: 'text-green-600' }}
        />
        <StatCard
          icon={BarChart3}
          label="Avg Order Value"
          value={formatCurrency(avgOrderValue)}
          accent={{ bg: 'bg-purple-100', icon: 'text-purple-600' }}
        />
      </div>
    </div>
  );
};

export default Payouts;
